import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Phone, ShieldCheck, Timer, Wallet, BadgeCheck, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import Chat from "@/components/Chat";
import { getPublicSettings } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1556745757-8d76bdb6984b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const DEFAULT = {
  nome_brand: "QuintoSubito", badge: "Verifica gratuita in 2 minuti",
  titolo: "Fino a 75.000 € con la cessione del quinto.", titolo_evidenziato: "Scopri subito se puoi averli.",
  sottotitolo: "Dipendente, pensionato o statale? Rispondi a poche domande e ricevi subito una risposta chiara, senza impegno e senza moduli infiniti.",
  cta: "Verifica ora, è gratis", cta_nota: "Nessun impegno · Risposta immediata · Dati protetti",
  punti: "Anche con altri finanziamenti in corso|Anche con segnalazioni CRIF|Rata trattenuta in busta paga o pensione|Consulente dedicato ti richiama",
  chat_titolo: "Rispondi e scopri la tua fattibilità", chat_sottotitolo: "Bastano pochi tocchi: scegli le risposte rapide, pensiamo noi al resto.",
  footer_testo: "Servizio di raccolta richieste per conto di intermediari autorizzati e iscritti OAM. Non costituisce erogazione diretta di credito. Dati trattati ai sensi del GDPR UE 2016/679.",
  contatto_telefono: "",
};

const STEPS = [
  { icon: Timer, t: "2 minuti", d: "Poche domande con risposte rapide, niente documenti ora." },
  { icon: BadgeCheck, t: "Esito immediato", d: "Sai subito se e quanto puoi ottenere." },
  { icon: Phone, t: "Ti richiamiamo", d: "Un consulente dedicato ti contatta per l'offerta." },
];

export default function Acchiappo() {
  const quizRef = useRef(null);
  const [A, setA] = useState(DEFAULT);

  useEffect(() => {
    document.title = `${A.nome_brand} — Cessione del quinto, verifica gratuita`;
  }, [A.nome_brand]);

  useEffect(() => {
    getPublicSettings().then((s) => setA({ ...DEFAULT, ...s.acchiappo })).catch(() => {});
  }, []);

  const go = () => quizRef.current?.scrollIntoView({ behavior: "smooth" });
  const punti = (A.punti || "").split("|").map((p) => p.trim()).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#fff8f1] text-stone-900" data-testid="acchiappo-page">
      <header className="sticky top-0 z-50 bg-[#fff8f1]/90 backdrop-blur border-b border-orange-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-extrabold text-xl tracking-tight" data-testid="acchiappo-brand">
            <Wallet className="h-6 w-6 text-orange-600" /> {A.nome_brand}
          </div>
          <div className="flex items-center gap-3">
            {A.contatto_telefono && (
              <a href={`tel:${A.contatto_telefono}`} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-stone-700" data-testid="acchiappo-phone">
                <Phone className="h-4 w-4" />{A.contatto_telefono}
              </a>
            )}
            <Button size="sm" onClick={go} className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-5" data-testid="acchiappo-nav-cta">Verifica gratis</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-stone-950" />
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/85 to-stone-950/40" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid lg:grid-cols-5 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-orange-500/15 border border-orange-400/30 text-orange-300 text-sm font-semibold px-4 py-1.5" data-testid="acchiappo-badge">
              <Timer className="h-4 w-4" /> {A.badge}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] mt-6" data-testid="acchiappo-title">
              {A.titolo} <span className="text-orange-400">{A.titolo_evidenziato}</span>
            </h1>
            <p className="text-lg sm:text-xl text-stone-300 mt-6 max-w-xl leading-relaxed">{A.sottotitolo}</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-8">
              <Button size="lg" onClick={go} className="bg-orange-500 hover:bg-orange-600 text-white text-base h-14 px-8 rounded-full gap-2 font-bold shadow-lg shadow-orange-500/30" data-testid="acchiappo-hero-cta">
                {A.cta} <ArrowRight className="h-5 w-5" />
              </Button>
              <span className="text-sm text-stone-300 inline-flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400" />{A.cta_nota}</span>
            </div>
          </motion.div>
          <motion.ul initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 rounded-3xl bg-white/5 border border-white/10 backdrop-blur p-6 space-y-3" data-testid="acchiappo-points">
            {punti.map((p) => (
              <li key={p} className="flex items-start gap-3 text-white"><CheckCircle2 className="h-5 w-5 text-orange-400 shrink-0 mt-0.5" /><span className="font-medium">{p}</span></li>
            ))}
          </motion.ul>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid sm:grid-cols-3 gap-6">
        {STEPS.map((s, i) => (
          <motion.div key={s.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="rounded-2xl bg-white border border-orange-100 p-6 shadow-sm">
            <div className="h-11 w-11 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center font-display font-extrabold">{i + 1}</div>
            <h3 className="font-display font-bold text-lg mt-4 flex items-center gap-2"><s.icon className="h-5 w-5 text-orange-600" />{s.t}</h3>
            <p className="text-sm text-stone-600 mt-1 leading-relaxed">{s.d}</p>
          </motion.div>
        ))}
      </section>

      <section ref={quizRef} className="py-14 sm:py-20 scroll-mt-16 bg-white border-y border-orange-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight" data-testid="acchiappo-chat-title">{A.chat_titolo}</h2>
            <p className="text-stone-600 mt-3">{A.chat_sottotitolo}</p>
          </div>
          <Chat assistantName={`Assistente ${A.nome_brand}`} source="acchiappo" />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid sm:grid-cols-3 gap-6 text-center">
        {[["12.000+", "verifiche effettuate"], ["4.8/5", "soddisfazione clienti"], ["< 24h", "richiamo del consulente"]].map(([n, l]) => (
          <div key={l}><div className="font-display text-4xl font-extrabold text-orange-600">{n}</div><div className="text-sm text-stone-600 mt-1 inline-flex items-center gap-1"><Star className="h-3.5 w-3.5" />{l}</div></div>
        ))}
      </section>

      <footer className="bg-stone-950 text-stone-400 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row gap-4 justify-between items-start">
          <div className="flex items-center gap-2 text-white font-display font-bold"><ShieldCheck className="h-5 w-5 text-orange-400" />{A.nome_brand}</div>
          <p className="text-xs max-w-xl leading-relaxed">{A.footer_testo} <Link to="/cookie-policy" className="underline">Cookie Policy</Link></p>
        </div>
      </footer>
    </div>
  );
}
